import React from 'react';
import {View, Text, StyleSheet, Pressable, TouchableOpacity, Image} from 'react-native';
import FastImage from 'react-native-fast-image';
import moment from 'moment-timezone';
import {useNavigation} from '@react-navigation/native';
import {Event} from '~types/api/event';
import {SCREENS} from '~constants';
import {NavigationProps} from '~types/navigation/navigation.type';
import useTabBar from '~containers/Core/navigation/AppScreens/zustand';
import {CheckIcon} from '~assets/icons';
import LikeButton from '~components/LikeButton';
import {AnimatedPressable, FadeIn} from '~components/MicroInteractions';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, typography, borderRadius, shadows, responsive} from '~constants';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {accessibility} from '~Utils/accessibility';
import {haptics} from '~Utils/haptics';
import Share from 'react-native-share';
import {useTranslation} from 'react-i18next';
import {useGetUserAccount} from '~hooks/user/useGetUser';
import {debounce} from '~Utils/viewCleanup';
import {useUserStore} from '~providers/userStore/zustand';
import ReportEventModal from '~components/ModalWithItems/ReportEventModal';
import {useReportEvent} from '~hooks/event/useReportEvent';
import {getMessageOfRecurrence} from '~Utils/event';
import {useSubscriptionContext} from '~contexts/SubscriptionContext';

interface ModernEventCardProps {
  item: Event;
  index: number;
  isMyEvents?: boolean;
  disableAnimation?: boolean;
  isPastEvent?: boolean;
}

const ModernEventCard: React.FC<ModernEventCardProps> = ({
  item,
  index,
  isMyEvents = false,
  disableAnimation = false,
  isPastEvent = false,
}) => {
  const navigation = useNavigation<NavigationProps>();
  const {setIsTabBarDisabled} = useTabBar();
  const {colors} = useTheme();
  const {t} = useTranslation();
  const user = useUserStore(state => state.user);
  const uid = user?.uid;
  const {data: userAccount} = useGetUserAccount(uid || '');
  const {reportEvent, isLoading: isReporting} = useReportEvent();
  const {activePlan, hasActivePlan} = useSubscriptionContext();
  

  // Report modal state
  const [isReportModalVisible, setIsReportModalVisible] = React.useState(false);

  // Event type detection
  const isPyxiSelectEvent = item?.host_id === 'CmCCvWvpRVhhFlHdgU9QE6hbun82';
  const isNeighbourHoodEvent = item?.host_id === 'bxcOenW6joNFwgiMBctR0zJT07F2';

  // Check if event is included in active subscription
  const isEventIncludedInSubscription = React.useMemo(() => {
    if (!hasActivePlan || !activePlan?.plan_quota_remaining_by_service_type) {
      return false;
    }

    const eventType = item?.event_service_type;
    const planQuota = activePlan.plan_quota_remaining_by_service_type;

    // Map event service types to subscription features
    const includesRestaurant = eventType === 'THE_REST_TABLE' && planQuota.THE_REST_TABLE;
    const includesHousewarming = eventType === 'HOUSEWARMING' && planQuota.HOUSEWARMING;
    const includesCultural = eventType === 'CULTURAL_EDIT' && planQuota.CULTURAL_EDIT;

    const featureMatches = includesRestaurant || includesHousewarming || includesCultural;

    return featureMatches;
  }, [hasActivePlan, activePlan, item?.event_service_type])

  const isEventRunning = () => {
    const now = moment();
    return now.isBetween(moment(item.start_date), moment(item.end_date));
  };

  const isEventEndingSoon = () => {
    const now = moment();
    const endDate = moment(item.end_date);
    const hoursUntilEnd = endDate.diff(now, 'hours');
    // Show "ending soon" if event ends within 24 hours and is currently running
    return isEventRunning() && hoursUntilEnd <= 24 && hoursUntilEnd > 0;
  };

  const handleEventPress = React.useCallback(() => {
    haptics.buttonPress();
    setIsTabBarDisabled(true);
    
    // If this is My Events and we have an order_id, navigate to PaymentSuccess
    if (isMyEvents && (item as any).order_id) {
      navigation.navigate(SCREENS.PAYMENT_SUCCESS, {
        order_id: (item as any).order_id,
      });
    } else {
      // Regular event navigation
      navigation.navigate(SCREENS.HOME_EVENT, {
        tag: `${item.event_id}=tag`,
        eventId: item.event_id,
        item: item,
      });
    }
  }, [item, navigation, setIsTabBarDisabled, isMyEvents]);

  const handleShare = React.useCallback(async () => {
    haptics.selection();

    try {
      if (!item?.event_id) {
        return;
      }

      // Use the same URL format as EventDetails
      const redirectLink = `https://partner.pyxi.ai/event/detail/${item.event_id}`;
      const url = redirectLink;

      // Get user name for personalized message
      const userName = userAccount?.last_name
        ? `${userAccount.first_name} ${userAccount.last_name}`
        : userAccount?.first_name || 'Someone';

      // Use the same sharing logic as EventDetails with internationalized message
      await Share.open({
        message: t('invite_message', {userName, itemName: item.name, url}),
      });
    } catch (error) {
      console.log('Error sharing:', error);
    }
  }, [item, userAccount, t]);

  const handleReport = React.useCallback(() => {
    haptics.selection();
    setIsReportModalVisible(true);
  }, []);

  const handleReportSubmit = React.useCallback(
    (reason: string) => {
      if (!uid || !item?.event_id) {
        return;
      }

      reportEvent({
        user_id: uid,
        event_id: item.event_id,
        reason: reason,
      });
      setIsReportModalVisible(false);
    },
    [uid, item?.event_id, reportEvent],
  );

  const formatDateRange = () => {
    const startDate = moment.utc(item.start_date);
    const endDate = moment.utc(item.end_date);
    
    // Same day format
    if (startDate.format('MMM DD, YYYY') === endDate.format('MMM DD, YYYY')) {
      return `${startDate.format('MMM DD')} · ${startDate.format('HH:mm')}-${endDate.format('HH:mm')}`;
    }
    
    // Different days format
    return `${startDate.format('MMM DD')} - ${endDate.format('MMM DD')} · ${startDate.format('HH:mm')}`;
  };

  const eventDate = formatDateRange();
  const accessibilityProps = accessibility.eventCardProps(
    item.name,
    eventDate,
    item.address_name,
    item.total_attendee_count,
  );

  const styles = createStyles(colors, isPastEvent);
  // Determine border color based on event type
  let cardBorderColor = colors.border + '';
  if (isPyxiSelectEvent) cardBorderColor = colors.eventPyxiSelect;
  else if (isNeighbourHoodEvent) cardBorderColor = colors.eventNeighbourhood;
  // Derive a display price from tickets if paid (show currency symbol, not code)
  const getDisplayPrice = (): string | null => {
    if (!item.is_paid || !item.tickets || item.tickets.length === 0) return null;

    // Filter to valid tickets (not removed)
    const valid = item.tickets.filter(t => !t.removed);
    if (valid.length === 0) return null;

    const primaryCurrency = (valid[0].currency || 'GBP').toUpperCase();

    const CURRENCY_SYMBOLS: Record<string, string> = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      CAD: '$',
      AUD: '$',
      NZD: '$',
      JPY: '¥',
      CNY: '¥',
      INR: '₹',
      KRW: '₩',
      RUB: '₽',
      TRY: '₺',
      VND: '₫',
      ILS: '₪',
      BRL: 'R$',
      ZAR: 'R',
      NGN: '₦',
      MXN: '$',
      CHF: 'CHF',
      SEK: 'kr',
      NOK: 'kr',
      DKK: 'kr',
      PLN: 'zł',
      CZK: 'Kč',
      HUF: 'Ft',
      THB: '฿',
      PHP: '₱',
      IDR: 'Rp',
      MYR: 'RM',
      SGD: 'S$',
      HKD: 'HK$',
      TWD: 'NT$',
      ARS: '$',
      CLP: '$',
      COP: '$',
      PEN: 'S/',
    };

    const getCurrencySymbol = (code: string): string => {
      // If code already contains a non A-Z char treat as symbol
      if (/[^A-Z]/.test(code)) return code;
      return CURRENCY_SYMBOLS[code] || code; // Fallback to code if unknown
    };

    const symbol = getCurrencySymbol(primaryCurrency);

    // Compute effective prices (use discounted_price if valid)
    const effectivePrices = valid.map(t => {
      const base = t.price;
      const discounted = t.discounted_price && t.discounted_price > 0 && t.discounted_price !== t.price ? t.discounted_price : null;
      return discounted ?? base;
    });
    const min = Math.min(...effectivePrices);
    const max = Math.max(...effectivePrices);

    const formatNumber = (v: number) => (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));

    // Some multi-char symbols (e.g. CHF, kr, zł) typically have a space before the number
    const needsSpace = /^(CHF|kr|zł|Kč|Ft|S\/|RM|Rp)$/.test(symbol);
    const join = (v: number) => needsSpace ? `${symbol} ${formatNumber(v)}` : `${symbol} ${formatNumber(v)}`;

    if (min === max) return join(min);
    return `${join(min)} - ${join(max)}`;
  };

  const displayPrice = getDisplayPrice();

  return (
    <FadeIn delay={disableAnimation ? 0 : index * 100} duration={300}>
      <TouchableOpacity activeOpacity={1} style={styles.container}>
        {/* Main Card Container */}
        <View style={[styles.card, isPastEvent && styles.pastEventCard, {borderColor: cardBorderColor}]}>
          {/* Top Price Pill / Subscription Badge */}
          {isEventIncludedInSubscription ? (
            null
          ) : displayPrice && (
            <View style={styles.pricePill}>
              <Text style={styles.pricePillText}>{displayPrice}</Text>
            </View>
          )}
          
          {/* Image Section */}
          <View style={styles.imageContainer}>
            <FastImage
              style={styles.eventImage}
              source={{
                uri: item.image_url,
                priority: 'high',
              }}
              resizeMode="cover"
            />
          </View>
          
          {/* Content Section */}
          <View style={styles.contentContainer}>
            {/* Event Title */}
            <Text style={styles.eventTitle} numberOfLines={2}>
              {item.name}
            </Text>
            
            {/* Location with Icon */}
            <View style={styles.locationContainer}>
              <Ionicons name="location-outline" size={16} color={colors.textSecondary} />
              <Text style={styles.locationText} numberOfLines={1}>{item.address_name || item.coord_address}</Text>
            </View>
            
            {/* Time with Icon */}
            <View style={styles.timeContainer}>
              <Ionicons name="time-outline" size={16} color={colors.textSecondary} />
              <Text style={styles.timeText}>{eventDate}</Text>
            </View>
            
            {/* Recurrence Details (if recurring event) */}
            {item?.event_group_id && (
              <View style={styles.recurrenceContainer}>
                <Ionicons name="repeat-outline" size={16} color={colors.primary} />
                <Text style={styles.recurrenceText}>{getMessageOfRecurrence(item?.event_group)}</Text>
              </View>
            )}
            
            {/* Tags Row */}
            <View style={styles.tagsContainer}>
              <View style={styles.tagPill}>
                <Text style={styles.tagText}>connection</Text>
              </View>
              <View style={styles.tagPill}>
                <Text style={styles.tagText}>conversation</Text>
              </View>
              <View style={styles.tagPill}>
                <Text style={styles.tagText}>energy-reset</Text>
              </View>
            </View>
            
            {/* Action Button */}
            <TouchableOpacity 
              style={styles.joinButton}
              onPress={handleEventPress}
              activeOpacity={0.8}
            >
              <Text style={styles.joinButtonText}>
                {t('home.view_details')}
              </Text>
              <Ionicons 
                name={isMyEvents ? 'receipt-outline' : 'eye-outline'} 
                size={20} 
                color={colors.white} 
                style={{marginLeft: 6}} 
              />
            </TouchableOpacity>
          </View>
          
          {/* Action Buttons */}
          {false && !isMyEvents && (
            <View style={styles.actionButtonsOverlay}>
              <TouchableOpacity
                onPress={handleShare}
                style={[styles.actionButton, {backgroundColor: 'rgba(255, 255, 255, 0.9)'}]}
                activeOpacity={0.7}
                {...accessibility.buttonProps('Share event', 'Share this event with friends')}>
                <Ionicons name="share-outline" size={20} color={colors.textPrimary} />
              </TouchableOpacity>
            </View>
          )}
        </View>

        {isNeighbourHoodEvent && (
          <View style={styles.neighbourHoodEvent}>
            <Text
              style={{
                color: '#ffff',
                fontSize: 13,
                fontWeight: 'bold',
              }}>
              {`Neighbourhood Event`}
            </Text>
          </View>
        )}

        {/* Report Modal */}
        <ReportEventModal
          isVisible={isReportModalVisible}
          close={() => setIsReportModalVisible(false)}
          onPress={handleReportSubmit}
        />
      </TouchableOpacity>
    </FadeIn>
  );
};

const createStyles = (colors: any, isPastEvent: boolean = false) =>
  StyleSheet.create({
    neighbourHoodEvent: {
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      padding: 6,
      borderRadius: 15,
      position: 'absolute',
      top: 43,
      right: 10,
      zIndex: 1,
    },
    selectIcon: {
      width: 50, 
      height: 25, 
      borderRadius: 25,
    },
    goingIconContainer: {
      zIndex: 1,
      position: 'absolute',
      top: 45,
      left: 10,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      height: 30,
      borderRadius: 15,
      alignItems: 'center',
      flexDirection: 'row',
      paddingHorizontal: 8,
    },
    container: {
      marginBottom: 8, // Further reduced margin
      borderRadius: borderRadius.lg, // Slightly reduced border radius
      marginHorizontal: 10, // Reduced horizontal margin
      overflow: 'hidden',
      backgroundColor: colors.surface,
      shadowColor: colors.black,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    },
    card: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg, // Slightly reduced border radius
      overflow: 'hidden',
      borderWidth: 0,
      borderColor: colors.border,
      position: 'relative',
    },
    pastEventCard: {
      opacity: 1,
      backgroundColor: colors.gray100,
    },
    pricePill: {
      position: 'absolute',
      right: 8,
      top: 8,
      backgroundColor: colors.white,
      paddingVertical: 4, // Smaller padding
      paddingHorizontal: 10, // Smaller padding
      borderRadius: 50,
      zIndex: 10,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 1, // Smaller shadow
      },
      shadowOpacity: 0.1, // Less opaque
      shadowRadius: 2, // Smaller radius
      elevation: 2, // Lower elevation
    },
    pricePillText: {
      color: colors.textPrimary,
      fontSize: typography.fontSize.xs, // Smaller font
      fontWeight: typography.fontWeight.bold,
    },
    strikethroughPrice: {
      textDecorationLine: 'line-through',
      color: colors.white
    },
    subscriptionBadge: {
      alignItems: 'center',
      backgroundColor: colors.primary,
    },
    subscriptionBadgeText: {
      color: colors.white,
    },
    imageContainer: {
      height: 130, // Further reduced height for more compact design
      position: 'relative',
      borderTopLeftRadius: borderRadius.xl,
      borderTopRightRadius: borderRadius.xl,
      overflow: 'hidden',
    },
    eventImage: {
      width: '100%',
      height: '100%',
    },
    contentContainer: {
      padding: spacing.sm, // Reduced horizontal padding
      paddingVertical: spacing.xs, // Further reduced vertical padding
      backgroundColor: colors.surface,
    },
    eventTitle: {
      fontSize: typography.fontSize.base, // Reduced font size
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      marginBottom: 4, // Smaller margin
    },
    locationContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 3, // Smaller margin
    },
    locationText: {
      color: colors.textSecondary,
      fontSize: typography.fontSize.xs, // Smaller font
      marginLeft: 4, // Smaller margin
      flex: 1,
    },
    timeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8, // Smaller margin
    },
    timeText: {
      color: colors.textSecondary,
      fontSize: typography.fontSize.xs, // Smaller font
      marginLeft: 4, // Smaller margin
      flex: 1,
    },
    recurrenceContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: spacing.md,
      backgroundColor: colors.gray100,
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.xs,
      borderRadius: borderRadius.sm,
    },
    recurrenceText: {
      color: colors.primary,
      fontSize: typography.fontSize.xs,
      marginLeft: spacing.xs,
      flex: 1,
      fontWeight: typography.fontWeight.medium,
    },
    tagsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: spacing.sm,
      gap: spacing.xs,
    },
    tagPill: {
      backgroundColor: colors.tagBg,
      paddingVertical: 5,
      paddingHorizontal: 12,
      borderRadius: 50,
    },
    tagText: {
      color: colors.primaryDarkText,
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.semibold,
    },
    joinButton: {
      backgroundColor: colors.warning,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 8, // Further reduced padding
      borderRadius: 6, // Smaller radius
      marginTop: 3, // Smaller margin top
    },
    joinButtonText: {
      color: colors.white,
      fontSize: typography.fontSize.sm, // Smaller font
      fontWeight: typography.fontWeight.bold,
    },
    actionButtonsOverlay: {
      position: 'absolute',
      top: spacing.md,
      right: spacing.sm,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      backgroundColor: 'transparent',
      zIndex: 5,
    },
    actionButton: {
      padding: spacing.xs,
      borderRadius: borderRadius.full,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    // Legacy styles for compatibility
    imageOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
    },
    clickableArea: {},
    eventDate: {
      color: colors.textSecondary,
    },
    pastEventText: {
      color: colors.textTertiary,
    },
    priceText: {
      color: colors.textPrimary,
      fontWeight: typography.fontWeight.bold,
    },
    priceContainer: {
      flex: 1,
    },
    bottomRowClickable: {},
    topRow: {},
    liveIndicator: {},
    liveDot: {},
    liveText: {},
    endingSoonIndicator: {},
    endingSoonText: {},
    titleOverlay: {},
    overlayTitle: {},
  });

export default ModernEventCard;
